"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useGlobalModalStack } from "@wtc/ui";
import { toast } from "sonner";
import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ModalRegistry, ModalDataFor } from "@/lib/modals/data/modal-data-types";
import type { BuildActionContextParams, DetailActionContext } from "./types";

export function useBuildActionContext<T extends BaseEntity>({
  item,
  hooks,
  refetch,
  updateMutation,
  deleteMutation,
  entityName,
  config,
  customComponents,
  onEditModalOpen,
  onEditModalClose,
  onDeleteComplete,
  isTicketInlineEditMode,
  onToggleTicketInlineEdit,
  hasTicketPendingChanges,
  onSaveTicketPending,
  isTaskInlineEditMode,
  onToggleTaskInlineEdit,
  hasTaskPendingChanges,
  onSaveTaskPending,
}: BuildActionContextParams<T>): DetailActionContext<T> | null {
  // Always call hooks at the top level - no conditional logic
  const router = useRouter();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useGlobalModalStack();

  return useMemo(() => {
    // Return null if no item, but hooks are still called consistently
    if (!item) return null;

    // Extract entity IDs from the item
    const entityIds = {
      contactId:
        (item as any).contact_id ||
        (item as any).id ||
        (entityName === "contacts" ? item.id : undefined),
      ticketId: (item as any).ticket_id || (entityName === "tickets" ? item.id : undefined),
      companyId: (item as any).company_id || (entityName === "companies" ? item.id : undefined),
      employeeId: (item as any).employee_id || (entityName === "employees" ? item.id : undefined),
      userId: (item as any).user_id || (entityName === "users" ? item.id : undefined),
      taskId: (item as any).task_id || (entityName === "tasks" ? item.id : undefined),
    };

    return {
      pageConfig: {
        entityName,
        config,
        hooks,
        customComponents,
      },
      item,
      entityName,
      entityIds,
      onRefresh: () => refetch(),
      onNavigate: (href: string) => router.push(href),
      onToast: (message: string, type: "success" | "error" | "info" | "warning" = "info") => {
        toast[type](message);
      },
      onOpenModal: (modalId: string, data?: Record<string, any>) => {
        if (modalId === "edit") {
          onEditModalOpen?.();
        } else {
          openModal(modalId, data);
        }
      },
      openModalTypeSafe: <T extends keyof ModalRegistry>(modalId: T, data?: ModalDataFor<T>) => {
        if (modalId === "edit") {
          onEditModalOpen?.();
        } else {
          openModal(modalId, data);
        }
      },
      onCloseModal: (modalId: string) => {
        if (modalId === "edit") {
          onEditModalClose?.();
        } else {
          closeModal(modalId);
        }
      },
      onUpdateItem: async (updates: Partial<T>) => {
        if (!item) throw new Error("No item to update");

        // Use EntityHooks interface - always use mutateAsync
        await updateMutation.mutateAsync({
          ...updates,
          id: item.id,
        });

        await refetch();
      },
      onDeleteItem: async () => {
        if (!item) throw new Error("No item to delete");

        // Use EntityHooks interface - always use mutateAsync
        await deleteMutation.mutateAsync(item.id);

        // Call the callback if provided
        if (onDeleteComplete) {
          onDeleteComplete();
        } else {
          // Default behavior: navigate back to list
          router.push(`/${entityName}`);
        }
      },
      isTicketInlineEditMode,
      onToggleTicketInlineEdit,
      hasTicketPendingChanges,
      onSaveTicketPending,
      isTaskInlineEditMode,
      onToggleTaskInlineEdit,
      hasTaskPendingChanges,
      onSaveTaskPending,
      queryClient,
    } as DetailActionContext<T>;
  }, [
    item?.id,
    hooks,
    refetch,
    router,
    entityName,
    config,
    customComponents,
    openModal,
    closeModal,
    updateMutation,
    deleteMutation,
    onEditModalOpen,
    onEditModalClose,
    onDeleteComplete,
    isTicketInlineEditMode,
    onToggleTicketInlineEdit,
    hasTicketPendingChanges,
    onSaveTicketPending,
    isTaskInlineEditMode,
    onToggleTaskInlineEdit,
    hasTaskPendingChanges,
    onSaveTaskPending,
    queryClient,
    // Note: entityIds are derived from item, so we don't need to add them to deps
  ]);
}
